// Real prior-season impact seeds, keyed by srID (Basketball-Reference slug).
//
// At league start a real player has no SIMULATED season yet, so the impact trade nudge and the
// contract-liability justification would be blind. But we ship the player's ACTUAL advanced
// stats (data/real-player-stats.json, via loadStats.basketball.ts). This builds, per player, a
// small SYNTHETIC stats array — his two most recent regular seasons (with VORP) plus his most
// recent playoff run — shaped exactly like player.stats, so it feeds impactFactor AND
// moneyJustification the same way real stats do. Independent of the `realStats` file setting
// (which is "none" by default on mobile, so player.stats would otherwise be empty).
//
// Once games are simulated, the player's own stats supersede these seeds automatically.

import { isSport } from "../../../common/sportFunctions.ts";
import loadStatsBasketball from "../realRosters/loadStats.basketball.ts";

let cache: { season: number; map: Map<string, any[]> } | undefined;
let loadFailed = false;

const regRow = (r: any) => ({
	season: r.season,
	playoffs: false,
	min: r.min,
	obpm: r.obpm,
	dbpm: r.dbpm,
	onOff100: r.onOff100,
	usgp: r.usgp,
	vorp: r.vorp,
});
const playoffRow = (r: any) => ({
	season: r.season,
	playoffs: true,
	min: r.min,
	obpm: r.obpm,
	dbpm: r.dbpm,
});

// Map srID -> a synthetic stats array (chronological: [prevReg?, latestReg, recentPlayoff?])
// drawn from the player's real seasons strictly before `season`. Returns undefined for
// non-basketball, or if the real-stats asset can't be loaded (fictional league / non-browser) —
// callers then fall back to neutral.
export const getRealImpactSeeds = async (
	season: number,
): Promise<Map<string, any[]> | undefined> => {
	if (!isSport("basketball") || loadFailed) {
		return undefined;
	}
	if (cache && cache.season === season) {
		return cache.map;
	}

	let data;
	try {
		data = await loadStatsBasketball();
	} catch {
		// No real-stats asset (fictional league / tests) — don't retry, fall back to neutral.
		loadFailed = true;
		return undefined;
	}

	// Group each player's usable rows once.
	const bySlug = new Map<string, { reg: any[]; po: any[] }>();
	for (const row of data.stats) {
		if (row.season >= season || (row.min ?? 0) <= 0) {
			continue;
		}
		let e = bySlug.get(row.slug);
		if (!e) {
			e = { reg: [], po: [] };
			bySlug.set(row.slug, e);
		}
		(row.playoffs ? e.po : e.reg).push(row);
	}

	const map = new Map<string, any[]>();
	for (const [slug, { reg, po }] of bySlug) {
		if (reg.length === 0) {
			continue;
		}
		reg.sort((a, b) => a.season - b.season);
		po.sort((a, b) => a.season - b.season);
		const latest = reg.at(-1);
		const prev = reg.at(-2);

		const synthetic: any[] = [];
		if (prev) {
			synthetic.push(regRow(prev));
		}
		synthetic.push(regRow(latest));

		// Include the most recent playoff run only if it's recent (see recentRows recency rule).
		const latestPo = po.at(-1);
		if (latestPo && latestPo.season >= latest.season - 1) {
			synthetic.push(playoffRow(latestPo));
		}

		map.set(slug, synthetic);
	}

	cache = { season, map };
	return map;
};
